import { useContext, useState } from 'react';
import styled from 'styled-components';
import { getUserV3Response, RemoveUserMutation, RemoveUserMutationVariables } from 'API';
import { removeUser } from 'graphql/mutations';
import { API } from 'aws-amplify';
import APIContext from 'context/APIContext';

import { useGetSingleUserAccess } from 'customHooks/useGetSingleUserAccess';
import DeleteConfirmationModal from 'components/DeleteConfirmationModall';
import { iconList } from 'assets/newAssets/SVG/additionalIconsForLinks/iconList';

const Card = styled.div`
  height: auto;
  min-height: 10vh;
  background: ${({ theme }) => theme.placeholderBackground};
  border-radius: 12px;
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  border: 1px solid ${({ theme }) => theme.border};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.background};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  .details {
    width: 75%;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;

    h1 {
      color: ${({ theme }) => theme.primaryGray};
      font-family: 'FontMedium', sans-serif;
      font-weight: 500;
      font-size: clamp(0.9rem, 1.1vw, 1.1rem);
      margin: 0 0 0.5rem 0;
      line-height: 1.4;
    }

    h2 {
      color: ${({ theme }) => theme.secondaryGray};
      font-family: 'FontRegular', sans-serif;
      font-weight: 400;
      font-size: clamp(0.8rem, 0.9vw, 0.95rem);
      margin: 0;
      line-height: 1.4;
    }
  }

  .action {
    position: absolute;
    bottom: 1.5rem;
    left: 1.5rem;
    display: flex;
    flex-direction: column;
  }

  .onoff {
    display: flex;
    flex-direction: row;
    gap: 0.75rem;
    width: 26vw;
    overflow-x: auto;
    padding: 0.5rem 0;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.border};
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.secondaryGray};
      border-radius: 2px;
      transition: background 0.2s ease;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${({ theme }) => theme.primaryGray};
    }

    button {
      all: initial;
      font-family: 'FontMedium', sans-serif;
      font-size: clamp(0.7rem, 0.8vw, 0.85rem);
      margin-bottom: 0.5rem;
      line-height: 1.2;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        background: ${({ theme }) => theme.border};
      }
    }
  }

  h4 {
    font-family: 'FontMedium', sans-serif;
    font-weight: 600;
    font-size: clamp(0.8rem, 0.9vw, 0.95rem);
    color: #22c55e;
    margin: 0;
  }

  .editing {
    width: 25%;
    height: fit-content;
    justify-content: flex-end;
    padding: 1.5rem;
    display: flex;
    gap: 0.5rem;
    color: ${({ theme }) => theme.primaryGray};

    .icons {
      display: flex;
      flex-direction: row;
      gap: 0.25rem;
      color: ${({ theme }) => theme.primaryGray};

      .icon {
        cursor: pointer;
        padding: 0.5rem;
        font-size: 1rem;
        border-radius: 8px;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: ${({ theme }) => theme.border};
          color: ${({ theme }) => theme.primaryGray};
        }

        &:active {
          transform: scale(0.95);
        }
      }
    }
  }

  .premium {
    position: absolute;
    background: linear-gradient(149deg, rgba(218, 165, 32, 1) 0%, rgba(255, 255, 255, 1) 24%);
    width: 100%;
    height: 100%;
    top: 10px;
    left: 0;
    filter: blur(10px);
    pointer-events: none;
  }
`;

type Props = {
  data: getUserV3Response;
  typeOfReadAccess: any[];
  userType: any;
  callFetchUsers(): any;
  modalModify(user: any, usrTyp: any, insightsData: any): any;
  setExternalUsers(props: any): any;
  setInternalUsers(props: any): any;
  setUserType(props: any): any;
  insightsData: any;
};

const UserAdminCard2 = (props: Props) => {
  const { getEnvironment } = useContext(APIContext);
  const [getAccess] = useGetSingleUserAccess();

  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [wait, setWait] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  // HANDLE REMOVE USER ENTIRELY
  const handleDeleteUser = (response: boolean) => {
    if (response) {
      props.setExternalUsers(undefined);
      props.setInternalUsers(undefined);
      removeTheUser();
      setWait(true);
    } else {
      setDeleteUserModal(false);
    }
  };
  // MUTATION REMOVE USER
  async function removeTheUser() {
    const remove = async (variables: any) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const response = (await API.graphql({
          query: removeUser,
          variables: variables,
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: RemoveUserMutation };
        return response;
      } catch (e) {
        console.error('ERROR IN REMOVING USER', e);
        throw e;
      }
    };

    const removeBatch = async (clientsBatch: any[]) => {
      const mail = props.data.Email?.startsWith('onewp_') ? props.data.Email.replace('onewp_', '') : props.data.Email;
      const vars = {
        Email: mail,
        Clients: clientsBatch,
        Environment: getEnvironment,
      } as RemoveUserMutationVariables;
      // console.log('VARS', vars);
      return await remove(vars);
    };

    try {
      const accessData = await getAccess(props.data);
      // console.log('props.data', accessData);

      const fullAccess = accessData
        .filter((e) => e?.Level === 'FULL_ACCESS')
        .map((e) => e?.Clients)
        .flat();
      const readAccess = accessData
        ?.filter((e) => e?.Level === 'READ_ACCESS')
        .map((e) => e?.Clients)
        .flat();
      const joined = fullAccess?.concat(readAccess);

      const removedTypename = joined?.map((e) => {
        const { __typename, ...rest } = e || {};
        return rest;
      });

      // Split into batches of 7
      const batchSize = 7;
      const batches = [];
      for (let i = 0; i < removedTypename.length; i += batchSize) {
        batches.push(removedTypename.slice(i, i + batchSize));
      }

      console.log(`Processing ${batches.length} batches of clients`);
      setDeleteProgress(0);

      // Process all batches
      for (let i = 0; i < batches.length; i++) {
        console.log(`Processing batch ${i + 1}/${batches.length}`);
        await removeBatch(batches[i]);

        // Update progress percentage
        const progressPercentage = Math.round(((i + 1) / batches.length) * 100);
        setDeleteProgress(progressPercentage);
      }

      // After all batches are processed successfully
      props.callFetchUsers();
      setDeleteUserModal(false);
      setWait(false);
      setDeleteProgress(0);
    } catch (e) {
      console.error('ERROR IN REMOVING USER BATCHES', e);
      setWait(false);
      setDeleteProgress(0);
    }
  }

  const isItReadPlus = (code: string) => {
    const getLevel = props.typeOfReadAccess.find((e) => e.ClientCode === code);
    if (getLevel?.Level === 'READ_PLUS_ACCESS') return '#DAA520';
    else return '#FFEE00';
  };

  return (
    <>
      <Card>
        <div className="details">
          <h1>{props.data.Name + ' ' + props.data.Family_name} </h1>
          <h2>{props.data.Email}</h2>
        </div>

        <div className="editing">
          <div className="icons">
            <div
              onClick={() => {
                props.modalModify(props.data, props.userType, props.insightsData);
              }}
              className="icon material-symbols-outlined"
            >
              {iconList.find((e) => e === 'edit')}
            </div>
            <div onClick={() => setDeleteUserModal(true)} className="icon material-symbols-outlined">
              {iconList.find((e) => e === 'delete')}
            </div>
          </div>
        </div>
      </Card>

      {deleteUserModal && (
        <DeleteConfirmationModal
          handleDeleteConfirmationModal={handleDeleteUser}
          modalObject={{
            title: 'Delete User',
            message: wait
              ? `Please wait while we delete the user${deleteProgress > 0 ? ` - ${deleteProgress}% completed` : '...'}`
              : 'Are you sure you want to delete this user?',
            mainButton: wait ? undefined : 'Delete',
            secondaryButton: wait ? undefined : 'Cancel',
          }}
        ></DeleteConfirmationModal>
      )}
    </>
  );
};

export default UserAdminCard2;
